import React, { useEffect, useState } from 'react';
import { PieChart, BarChart, Pie, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import { DownloadOutlined } from '@ant-design/icons';
import './SampleDetail.css';

const SampleDetail = (props) => {
  const { run } = props.match.params;
  const [sampleData, setSampleData] = useState(null);
  const [speciesData, setSpeciesData] = useState([]);
  const [binData, setBinData] = useState([]);
  const [pathopoiesisData, setPathopoiesisData] = useState([]);
  const [metadata, setMetadata] = useState({
    run: '',
    organism: '',
    bioProject: '',
    projectDescription: '',
    instrument: '',
    avgSpotLen: 0,
    base: 0,
    geoLocName: '',
    sampleUrl: '',
    projectUrl: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const metaRes = await axios.get(`/api/sample/${run}/metadata`);
        setMetadata(metaRes.data);
        console.log("metadata", metaRes.data);

        const sampleRes = await axios.get(`/api/sample/${run}`);
        setSampleData(sampleRes.data.sample || {});
        setSpeciesData(sampleRes.data.species || []);

        const binRes = await axios.get(`/api/sample/${run}/bins`);
        setBinData([
          { name: 'High Quality', value: binRes.data.highQualityBins || 0 },
          { name: 'Medium Quality', value: binRes.data.mediumQualityBins || 0 },
          { name: 'Low Quality', value: binRes.data.lowQualityBins || 0 }
        ]);

        const pathoRes = await axios.get(`/api/sample/${run}/pathopoiesis`);
        setPathopoiesisData([
          { name: 'Pathogenic', value: pathoRes.data.pathogenicBins || 0 },
          { name: 'Non-Pathogenic', value: pathoRes.data.nonPathogenicBins || 0 }
        ]);

      } catch (err) {
        setError("Failed to fetch data: " + err.message);
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [run]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const topSpecies = speciesData.slice(0, 10);
  const otherAbundance = Math.max(0, 1 - topSpecies.reduce((sum, species) => 
    sum + (species.relativeAbundance || 0), 0));

  const chartData = [
    ...topSpecies.map(species => ({
      name: species.genusName || 'Unknown',
      value: species.relativeAbundance || 0,
      abundance: (species.relativeAbundance * 100).toFixed(2) + '%'
    })),
    { name: 'Other', value: otherAbundance, abundance: (otherAbundance * 100).toFixed(2) + '%' }
  ];

  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', 
    '#A28DFF', '#FF6E83', '#6ECBFF', '#A0E6A0',
    '#FFB347', '#B39DDB', '#F06292'
  ];

  return (
    <div className="sample-detail-container">
      <header className="page-header">
        <h1>Sample Detail: {metadata.run || run}</h1>
      </header>

      {/* Metadata Section */}
      <section className="metadata-section">
        <div className="metadata-card">
          <div className="metadata-group">
            <h2>Run details</h2>
            <div className="metadata-row">
              <span className="metadata-label">Run ID:</span>
              <span className="metadata-value">{metadata.run || 'N/A'}</span>
            </div>
            <div className="metadata-row">
              <span className="metadata-label">Orgamism:</span>
              <span className="metadata-value">{metadata.organism || 'N/A'}</span>
            </div>
            <div className="metadata-row">
              <span className="metadata-label">In project:</span>
              <span className="metadata-value">{metadata.bioProject || 'N/A'}</span>
            </div>
            <div className="metadata-row">
              <span className="metadata-label">Instrument model:</span>
              <span className="metadata-value">{metadata.instrument || 'N/A'}</span>
            </div>
            <div className="metadata-row">
              <span className="metadata-label">AverageSpotLength:</span>
              <span className="metadata-value">
                {Math.floor(metadata.avgSpotLen) || 'N/A'}
              </span>
            </div>
            <div className="metadata-row">
              <span className="metadata-label">BaseNumber:</span>
              <span className="metadata-value">
                {metadata.base || 'N/A'}
              </span>
            </div>
            <div className="metadata-row">
              <span className="metadata-label">Geo location:</span>
              <span className="metadata-value">{metadata.geoLocName || 'N/A'}</span>
            </div>
            <div className="metadata-row">
              <span className="metadata-label">Sample URL:</span>
              <span className="metadata-value">
                {metadata.sampleUrl ? (
                  <a href={metadata.sampleUrl} target="_blank" rel="noopener noreferrer">
                    {metadata.sampleUrl}
                  </a>
                ) : (
                  'N/A'
                )}
              </span>
            </div>
            <div className="metadata-row">
              <span className="metadata-label">Project URL:</span>
              <span className="metadata-value">
                {metadata.projectUrl ? (
                  <a href={metadata.projectUrl} target="_blank" rel="noopener noreferrer">
                    {metadata.projectUrl}
                  </a>
                ) : (
                  'N/A'
                )}
              </span>
            </div>
          </div>

          <div className="metadata-group">
            <h2>Project information</h2>
            <div className="metadata-row">
              <span className="metadata-label">Project information:</span>
              <span className="metadata-value">{metadata.projectDescription || 'N/A'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Visualization Section */}
      <section className="visualization-section">
        <div className="chart-container">
          <h2>Species Relative Abundance</h2>
          <div className="charts-row">
            {/* <div className="chart-wrapper pie-chart-wrapper">
              <PieChart width={400} height={400}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, abundance }) => `${name}: ${abundance}`}
                  outerRadius={120}
                  innerRadius={60}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name, entry) => [(value * 100).toFixed(2) + '%', entry.payload.name]} />
                <Legend layout="vertical" align="right" verticalAlign="middle" />
              </PieChart>
            </div> */}

            <div className="chart-wrapper pie-chart-wrapper">
                <PieChart width={500} height={450}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  // label={({ name, abundance }) => `${name}: ${abundance}`}
                  label={false}
                  outerRadius={120}
                  innerRadius={60}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name, entry) => [(value * 100).toFixed(2) + '%', entry.payload.name]} />
                {/* <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const item = payload[0];
                      const percent = (item.value * 100).toFixed(2);
                      return (
                        <div style={{ fontSize: '12px', padding: '4px 8px' }}>
                          <div>{item.payload.name}</div>
                          <div>Abundance: {percent}%</div>
                        </div>
                      );
                    }
                    return null;
                  }}
                /> */}
                <Legend 
                  layout="horizontal" 
                  verticalAlign="bottom" 
                  align="center"
                  wrapperStyle={{ paddingTop: '20px' }}
                />
              </PieChart>
            </div>

            <div className="chart-wrapper bar-chart-wrapper">
              <BarChart
                width={500}
                height={400}
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} interval={0} />
                <YAxis label={{ value: 'Relative Abundance', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, name, entry) => [(value * 100).toFixed(2) + '%', entry.payload.name]} />
                <Bar dataKey="value" name="Abundance">
                  {chartData.map((entry, index) => (
                    <Cell key={`bar-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </div>
          </div>
        </div>

        <div className="chart-container">
          <h2>Bin Quality Distribution</h2>
          <BarChart
            width={600}
            height={400}
            data={binData}
            margin={{ top: 20, right: 30, left: 40, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Bar dataKey="value" name="Bin Count" fill="#8884d8" />
          </BarChart>
        </div>

        {/* <div className="chart-container">
          <h2>Pathopoiesis Distribution</h2>
          <p style={{margin: '0 0 20px 0', fontSize:14, color:'#555'}}>
            Pathogenicity of high- and medium-quality bins predicted using DCiPatho
          </p>
          <div className="charts-row">
            <div className="chart-wrapper">
              <PieChart width={300} height={300}>
                <Pie
                  data={pathopoiesisData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  // label={({ name, value }) => `${name}: ${value}`}
                  label={false}
                  outerRadius={100}
                  innerRadius={50}
                  dataKey="value"
                >
                  {pathopoiesisData.map((entry, index) => (
                    <Cell key={`patho-cell-${index}`} fill={index === 0 ? '#FF6B6B' : '#4ECDC4'} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, 'Count']} />
                <Legend />
              </PieChart>
            </div>
            <div className="chart-wrapper">
              <BarChart
                width={400}
                height={300}
                data={pathopoiesisData}
                margin={{ top: 20, right: 30, left: 40, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" name="Count">
                  {pathopoiesisData.map((entry, index) => (
                    <Cell key={`patho-bar-${index}`} fill={index === 0 ? '#FF6B6B' : '#4ECDC4'} />
                  ))}
                </Bar>
              </BarChart>
            </div>
          </div>
        </div> */}


        {/* UpSet SVG独立图片卡片 */}
        {/* <div className="chart-container">
          <h2>Phylogenetic Topology</h2>
          <p style={{marginTop:12, fontSize:13, color:'#666'}}>
              Phylogenetic tree of high- and medium-quality bins
          </p>
          <div style={{border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', background:'#fff'}}>
            {run && (
              <img
                src={`http://47.103.129.131:5001/svg/${run}.svg`}
                alt={`${run} habitat co-occurrence upset figure`}
                style={{width:'100%', maxWidth:'800px', display:'block', margin:'0 auto'}}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
            )}
            <div style={{display:'none', textAlign:'center', padding:'40px', color:'#666'}}>
              Analysing...
            </div>
            
          </div>
        </div> */}
      </section>

      <section className="download-section">
        <h2>Download Data</h2>
        <div className="download-buttons">
          <a
            className="download-btn"
            href={`/api/download/checkm/${encodeURIComponent(run)}`}
            download
          >
            <DownloadOutlined />
            Download CheckM Result
          </a>
        </div>
      </section>
    </div>
  );
};

export default SampleDetail;
